import debounce from 'lodash.debounce';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { renderComfirmationDialog } from 'reactApp/components/BaseConfirmDialog/BaseConfirmDialog.helpers';
import { EButtonType } from 'reactApp/components/BaseConfirmDialog/BaseConformationDialog.types';
import { ROOT_FOLDER_ID } from 'reactApp/constants/magicIdentificators';
import { getFolderPath, isMediaFolder } from 'reactApp/modules/file/utils';
import { checkUpdateLicenseRequest } from 'reactApp/modules/profile/profile.module';
import { historyPush } from 'reactApp/modules/router/router.module';
import { getCurrentStorage } from 'reactApp/modules/router/router.selectors';
import { getSearchRequestParams } from 'reactApp/modules/search/search.selectors';
import { SearchItem } from 'reactApp/modules/search/search.types';
import { setQueryParams } from 'reactApp/modules/settings/settings.module';
import { SettingsSelectors } from 'reactApp/modules/settings/settings.selectors';
import { EViewMode } from 'reactApp/modules/settings/settings.types';
import { getStorage } from 'reactApp/modules/storage/storage.helpers';
import { scrollToItemAction } from 'reactApp/modules/storage/storage.module';
import { getItemById, getStorageItemById } from 'reactApp/modules/storage/storage.selectors';
import { EStorageType } from 'reactApp/modules/storage/storage.types';
import { openViewer } from 'reactApp/modules/viewer/viewer.module';
import { ViewerSelectors } from 'reactApp/modules/viewer/viewer.selectors';
import { RootState } from 'reactApp/store';
import { logHandler } from 'reactApp/ui/Datalist/DataList.helpers';
import { sendDatalistGa } from 'reactApp/utils/datalistGa';
import { sendGa } from 'reactApp/utils/ga';
import { ECategoryGa, sendPaymentGa } from 'reactApp/utils/paymentGa';
import { decodeURIComponentHelper, getEncodePath, getHomeFolderPath } from 'reactApp/utils/urlHelper';

type LoadToItemReturnType = [number | null, boolean, (id: number | null) => void];

export const useLoadToItem = (items: string[], loadOnScroll: () => void, isLoading?: boolean): LoadToItemReturnType => {
    const [activeIndex, setActiveIndex] = useState<number | null>(-1);
    const [isElementLoaded, setElementLoaded] = useState<boolean>(false);
    const queryParams = useSelector(SettingsSelectors.getQueryParams);
    const loadMore = useMemo(() => (isLoading ? (): Promise<void> => Promise.resolve() : loadOnScroll), [isLoading, loadOnScroll]);
    const action = queryParams?.action;
    const itemRef = useRef<string>(decodeURIComponentHelper(queryParams?.item || ''));
    const item: string = decodeURIComponentHelper(queryParams?.item || '');

    useEffect(() => {
        if (!item || action !== 'scroll-to') {
            setElementLoaded(true);
            return;
        }

        if (activeIndex === null || isElementLoaded) {
            return;
        }

        const itemIndex = items.map((item) => item.toLowerCase()).indexOf(item.toLowerCase());

        if (itemIndex === -1) {
            loadMore();
            return;
        }

        setElementLoaded(true);
        setActiveIndex(itemIndex);
    }, [isLoading, loadMore, activeIndex, item, isElementLoaded, action, items]);

    /**
     * В виде тамбами и списком в элементы допушивается фейковый элемент,
     * а при виде галлереей - нет
     * Получается, элемент может иметь разный индекс при виде галереей и виде списком
     * Этот хук вызывается чтобы перенайти индекс в новой конфигурации
     */
    useEffect(() => {
        if (activeIndex === -1 || activeIndex === null) {
            return;
        }

        const itemIndex = items.map((item) => item.toLowerCase()).indexOf(itemRef.current.toLowerCase());

        if (itemIndex === -1 || itemIndex === activeIndex) {
            return;
        }

        setActiveIndex(itemIndex);
    }, [items, activeIndex, item]);

    return [activeIndex, isElementLoaded, setActiveIndex];
};

export const useScrollToItem = ({ activeIndex, dispatch, listRef, isElementLoaded }) => {
    const { item: itemParam, ...queryParams } = useSelector(SettingsSelectors.getQueryParams) || {};
    const hasScrolled = useRef(false);

    useEffect(() => {
        if (activeIndex === null) {
            dispatch(setQueryParams(queryParams));
            dispatch(scrollToItemAction(''));
            return;
        }

        const item = decodeURIComponentHelper(itemParam || '');

        if (item) {
            const path = getFolderPath(item);
            dispatch(historyPush({ id: getEncodePath(getHomeFolderPath(path === ROOT_FOLDER_ID ? path : `${path}/`)) }));
        }

        if (isElementLoaded && activeIndex >= 0 && !hasScrolled.current) {
            dispatch(setQueryParams(queryParams));
            hasScrolled.current = true;
            listRef.current?.scrollToItem(activeIndex);
        }
    }, [activeIndex, dispatch, isElementLoaded, listRef, itemParam]);
};

export const useSetActiveIndex = ({ selectedItem, items, setActiveIndex, storage }) => {
    useEffect(() => {
        const { isHome, isPublic } = getStorage(storage);
        if (!selectedItem || !items.includes(selectedItem) || !(isHome || isPublic)) {
            return;
        }

        const selectedIndex = items.indexOf(selectedItem);

        setActiveIndex(selectedIndex);
    }, [selectedItem, storage]);
};

export const useSetInitialViewerId = ({ isLoaded, loadOnScroll, initialViewerId, setInitialViewerId, items, storage, dispatch }) => {
    const initialViewerItem = useSelector((state: RootState) => getItemById(state, initialViewerId));
    const [updateLicenseCheck, setUpdateLicenseCheck] = useState<boolean>(false);
    const isViewerOpening = useSelector(ViewerSelectors.isViewerOpening);

    useEffect(() => {
        if (isLoaded && !initialViewerId && !updateLicenseCheck) {
            setUpdateLicenseCheck(true);
            dispatch(checkUpdateLicenseRequest());
            return;
        }

        if (isLoaded && initialViewerId && initialViewerItem?.isFolder) {
            setInitialViewerId('');
            setUpdateLicenseCheck(true);
            dispatch(checkUpdateLicenseRequest());
            return;
        }

        if (isLoaded && initialViewerId && !items.includes(initialViewerId)) {
            loadOnScroll();
            return;
        }

        if (isLoaded && initialViewerId && items.includes(initialViewerId)) {
            const data = {
                itemId: initialViewerId,
                itemStorage: storage || null,
                gaSuffix: '',
                itemIds: [],
            };

            if (!isViewerOpening) {
                dispatch(openViewer(data));
            }

            setInitialViewerId('');
        }
    }, [items, initialViewerId, isLoaded, initialViewerItem, isViewerOpening, loadOnScroll, setInitialViewerId]);
};

export const useHandleStopDrag = ({ isDragging, handleStopDrag, selectedItems }) => {
    useEffect(() => {
        if (isDragging) {
            document.addEventListener('mouseup', handleStopDrag);
        }

        return () => {
            document.removeEventListener('mouseup', handleStopDrag);
        };
    }, [isDragging, selectedItems]);
};

export const useHandleMouse = ({ handleMouseMove, handleMouseUp }) => {
    useEffect(() => {
        return function cleanup(): void {
            document.removeEventListener('mousemove', handleMouseMove);
            document.removeEventListener('mouseup', handleMouseUp);
        };
    }, []);
};

export const useHandlerOnDocumentClick = ({ handleOnDocumentClick }) => {
    useEffect(() => {
        document.addEventListener('click', handleOnDocumentClick, { capture: true });

        return () => {
            document.removeEventListener('click', handleOnDocumentClick, { capture: true });
        };
    }, [handleOnDocumentClick]);
};

export const useLogHandler = ({ items, viewMode, startTime, folder }) => {
    useEffect(() => {
        if (items.length) {
            logHandler({
                radarName: 'datalist_render',
                radars: [`${viewMode}=${Date.now() - startTime}`],
            });
        }
        if (folder?.id === ROOT_FOLDER_ID) {
            logHandler({
                radarName: 'createnew_render',
            });
        }
    }, [folder?.id, viewMode]);
};

export const useGaSelectSend = ({ selectedCount }) => {
    const isGaSelectSend = useRef<boolean>(false);

    useEffect(() => {
        if (selectedCount === 0 && isGaSelectSend.current) {
            isGaSelectSend.current = false;
        }
        if (selectedCount > 1 && !isGaSelectSend.current) {
            sendDatalistGa('start-selection');
            isGaSelectSend.current = true;
        }
    }, [selectedCount, isGaSelectSend]);
};

export const useGaShowGallery = ({ viewMode }) => {
    useEffect(() => {
        if (viewMode === EViewMode.gallery) {
            sendGa('datalist', 'show', viewMode);
        }
    }, [viewMode]);
};

export const useSendGaStat = ({ folder }) => {
    const isMedia = isMediaFolder(folder);

    useEffect(() => {
        if (isMedia) {
            sendGa('datalist', 'ismedia');
        }
    }, [folder?.id, isMedia]);
};

const sendSearchDwh = ({ xPageId, xReqId, start, data, query, count }) => {
    sendPaymentGa({
        eventCategory: ECategoryGa.entered,
        action: 'result-search',
        page_id: xPageId,
        req_id: xReqId,
        search_phrase: query,
        count_results: data.length,
        res_count: count,
        page: 1,
        results: data.map((item, index) => ({
            pos: start + index,
            hash: !item.isFolder ? item.hash : undefined,
            kind: item.kind,
            type: !item.isFolder ? item.ext : undefined,
            mtime: !item.isFolder ? item.mtime : undefined,
            size: !item.isFolder ? item.size : undefined,
        })),
    });
};

export const useSendDwh = <T>({
    storage,
    isSearchActive,
    isLoaded = false,
    isError,
    query,
    items,
}: {
    storage: EStorageType;
    isSearchActive: boolean;
    isLoaded?: boolean;
    isError: boolean;
    query?: string;
    items: T[];
}) => {
    // хранилище дипазона индексов отпралвенных / готовых к отправке элементов
    // result используется как кранилище id элементов готовых к отправке
    const [range, setRange] = useState<{ start: number; stop: number; results: T[]; isEmpty: boolean }>({
        start: 0,
        stop: 0,
        results: [],
        isEmpty: false,
    });
    const { xPageId, xReqId } = useSelector(getSearchRequestParams);

    // полчаем id ещё не отправленных элементов при их первом рендере в virtual list
    // при повторном рендере - скролл вверх списка - статистика отправляться не будет
    const onItemsRendered = useCallback(
        ({ stop }) => {
            setRange((state) => {
                const wasEmpty = state.stop === 0;
                const firstSingleResult = wasEmpty && items.length === 1;

                if (stop > state.stop || firstSingleResult) {
                    const start = wasEmpty ? 0 : state.stop + 1;
                    return { start, stop, results: items.slice(start, stop + 1), isEmpty: false };
                }

                return state;
            });
        },
        [items]
    );

    const data = useSelector((state: RootState) => range.results.map((id) => getStorageItemById(state, storage, id))) as SearchItem[];

    // сброс счётчика при изменении запроса
    useEffect(() => setRange({ start: 0, stop: 0, results: [], isEmpty: false }), [query]);

    // реакция на рендер новых элементов в virtual list
    useEffect(() => {
        if (isSearchActive && (range.results.length || range.isEmpty)) {
            sendSearchDwh({ xPageId, xReqId, start: range.start, data, query, count: items.length });
            // очищаем очередь результатов на отправку
            setRange((state) => ({ ...state, isEmpty: false, results: [] }));
        }
    }, [range]);

    useEffect(() => {
        if (isSearchActive && isLoaded && !isError && items.length === 0 && !range.isEmpty) {
            // ничего не найдено
            setRange((state) => ({ ...state, isEmpty: true }));
        }
    }, [isLoaded]);

    return { onItemsRendered };
};

const renderDialogDebounced = debounce((onSuccess) => {
    renderComfirmationDialog({
        dataQAId: 'change-viewmode',
        renderHeader: () => `Смена отображения`,
        renderContent: () => `Вы загрузили файлы, отличные от фото или видео. Для удобного просмотра формат отображения был изменен.`,
        successButtonText: 'Понятно',
        closable: false,
        buttons: [EButtonType.accept],
        onSuccess,
    });
}, 500);

export const useAutoSwitchFromGallery = ({ folder, viewMode, storage }) => {
    const [prevFolderId, setPrevFolderId] = useState<string>(folder?.id);
    const [isPopupDelayed, setIsPopupDelayed] = useState(false);

    const isMediaCurrent = isMediaFolder(folder);
    const newMediaView = isMediaCurrent && viewMode === EViewMode.gallery;
    const [isMediaView, setIsMediaView] = useState(newMediaView);
    const currentStorage = useSelector(getCurrentStorage);

    const folderId = folder?.id;

    // Папка была медиа и был включен режим галерея, а стала вдруг не медиа, тогда попап
    if (
        currentStorage !== EStorageType.start &&
        isMediaView &&
        !isMediaCurrent &&
        prevFolderId === folderId &&
        !isPopupDelayed &&
        (storage === EStorageType.home || storage === EStorageType.public)
    ) {
        renderDialogDebounced(() => setIsPopupDelayed(false));
        setIsPopupDelayed(true);
    }

    useEffect(() => {
        if (isPopupDelayed && prevFolderId !== folderId) {
            setIsPopupDelayed(false);
            renderDialogDebounced.cancel();
        }
    }, [isPopupDelayed, prevFolderId, folderId]);

    useEffect(() => {
        if (prevFolderId !== folderId) {
            setPrevFolderId(folderId);
        }
        if (isMediaView !== newMediaView) {
            setIsMediaView(newMediaView);
        }
    }, [folderId, newMediaView, setPrevFolderId, prevFolderId, isMediaView, setIsMediaView]);
};
